import {
  ApiClientError,
  ApiFetchError,
  apiClient,
} from '@borradh-workspace/api-client';
import { KeyRound } from 'lucide-react';
import { useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Field, FieldError, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { setAdminTabToken } from '@/lib/admin-tab-token';

interface TwoFactorVerifyProps {
  onVerified: () => void;
}

/**
 * Turn a verify-2fa failure into something that points at the real cause.
 *
 * Rate limiting deserves its own message above all: retrying is exactly the
 * wrong response to a 429, and "invalid code" invites precisely that.
 */
function describeVerifyFailure(err: unknown): string {
  if (err instanceof ApiFetchError || err instanceof ApiClientError) {
    if (err.status === 403) {
      return 'This account does not have admin access in this environment.';
    }
    if (err.status === 429) {
      return 'Too many attempts. Wait a few minutes before trying again.';
    }
    if (err.status === 401 && err.message && !/invalid/i.test(err.message)) {
      return err.message;
    }
    if (err.status === 400 || err.status === 401) {
      return 'Invalid code. Please try again.';
    }
    if (err.status >= 500) {
      return 'Verification is unavailable right now. Try again shortly.';
    }
  }
  return 'Could not verify your code. Check your connection and try again.';
}

export function TwoFactorVerify({ onVerified }: TwoFactorVerifyProps) {
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const submitting = useRef(false);

  const handleVerify = async () => {
    // Enter can submit again while the button is disabled. Keep one request
    // in flight so a second keypress cannot consume the limited TOTP attempts.
    if (submitting.current || code.length !== 6) return;
    submitting.current = true;
    setIsLoading(true);
    setError('');
    try {
      const result = await apiClient.post<{ token?: string }>(
        'admin-terminal/verify-2fa',
        { code },
        // Forces the API to hand back this tab's own copy of its session —
        // see admin-tab-token.ts for why every admin-terminal tab needs one.
        { headers: { 'X-Client-Type': 'mobile' } }
      );
      if (result?.token) setAdminTabToken(result.token);
      onVerified();
    } catch (err) {
      // A bare catch here used to render "Invalid code" for EVERY failure —
      // including a 429 from the 5-per-15-min throttle and the 401 raised when
      // the forwarded session belongs to someone else. Both were reported as
      // "it keeps saying invalid code" while the API logged something else
      // entirely, and the screen actively pointed away from the real cause.
      setError(describeVerifyFailure(err));
    } finally {
      submitting.current = false;
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-[60vh] items-center justify-center p-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center">
          <div className="bg-primary/10 mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full">
            <KeyRound className="text-primary h-6 w-6" />
          </div>
          <CardTitle>Admin Verification</CardTitle>
          <CardDescription>
            Enter your authenticator code to access the admin terminal.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form
            className="space-y-4"
            aria-busy={isLoading}
            onSubmit={(event) => {
              event.preventDefault();
              void handleVerify();
            }}
          >
            <Field>
              <FieldLabel htmlFor="totp-code">6-digit code</FieldLabel>
              <Input
                id="totp-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                placeholder="000000"
                className="text-center font-mono text-lg tracking-widest"
                autoFocus
              />
              {error && <FieldError errors={[{ message: error }]} />}
            </Field>
            <Button
              type="submit"
              disabled={code.length !== 6 || isLoading}
              className="w-full"
            >
              {isLoading ? 'Verifying...' : 'Verify'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
